// apps/client/src/utils/edmHtmlGuide.ts
//
// Plan-v3 core Slice E1 (edm_content): the HTML authoring guide shown beside
// "Paste your own HTML", and the "Copy brief for Claude" text.
//
// The allowlists below MIRROR the server sanitiser
// (services/client-functions/src/edm/sanitizeEmailHtml.ts: EMAIL_SANITIZE
// allowedTags / allowedAttributes / allowedSchemesByTag / nonTextTags and
// STYLE_ALLOWLIST). They are exported so a client-functions test can compare
// them with the sanitiser constants: if the sanitiser changes, that test
// fails until this guide says the same thing. The guide only describes; the
// sanitiser decides.
//
// Pure module: no React, no Firebase.

import { EDM_MERGE_FIELDS } from './edm';

/** Tags the sanitiser keeps (sanitizeEmailHtml.ts allowedTags). */
export const EDM_GUIDE_ALLOWED_TAGS: readonly string[] = [
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
  'div', 'span', 'p', 'br', 'hr', 'center', 'font', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'small', 'sup', 'sub', 'blockquote', 'pre', 'code',
  'ul', 'ol', 'li', 'a', 'img',
];

/** Attributes the sanitiser keeps, per tag ('*' = every allowed tag) (allowedAttributes). */
export const EDM_GUIDE_ALLOWED_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = {
  '*': ['style', 'align', 'valign', 'width', 'height', 'bgcolor', 'dir', 'class', 'id', 'role',
    'aria-label', 'aria-hidden', 'title', 'lang'],
  table: ['border', 'cellpadding', 'cellspacing'],
  td: ['colspan', 'rowspan', 'nowrap'],
  th: ['colspan', 'rowspan', 'scope'],
  font: ['color', 'face', 'size'],
  a: ['href', 'target', 'name', 'rel'],
  img: ['src', 'alt', 'width', 'height', 'border'],
};

/** Inline CSS properties the sanitiser keeps (STYLE_ALLOWLIST keys). Values may never contain `(` except rgb()/rgba() colours. */
export const EDM_GUIDE_ALLOWED_CSS: readonly string[] = [
  'color', 'background-color', 'font-family', 'font-size', 'font-weight', 'font-style',
  'line-height', 'letter-spacing', 'text-align', 'text-decoration', 'text-transform',
  'vertical-align', 'width', 'height', 'max-width', 'min-width',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-collapse', 'border-radius', 'display',
];

/** Link schemes kept on `<a href>` (allowedSchemesByTag.a). */
export const EDM_GUIDE_LINK_SCHEMES: readonly string[] = ['http', 'https', 'mailto'];

/** Image schemes kept on `<img src>` (allowedSchemesByTag.img); any other image is removed. */
export const EDM_GUIDE_IMAGE_SCHEMES: readonly string[] = ['https'];

/** Elements removed together with everything inside them (nonTextTags). */
export const EDM_GUIDE_REMOVED_WITH_CONTENT: readonly string[] = [
  'script', 'style', 'textarea', 'option', 'noscript', 'title', 'head', 'template', 'xmp',
  'iframe', 'object', 'embed', 'svg', 'math',
];

/** The size the server refuses above (EDM_MAX_CUSTOM_HTML_BYTES). */
export const EDM_GUIDE_MAX_BYTES = 200 * 1024;
/** What to aim for: Gmail clips messages over about 102 KB including our footer. */
export const EDM_GUIDE_TARGET_BYTES = 80 * 1024;
/** Layout width to design for. */
export const EDM_GUIDE_WIDTH_PX = 600;

/** Merge fields, written exactly as they go in the HTML. */
export const EDM_GUIDE_MERGE_TOKENS: readonly string[] = EDM_MERGE_FIELDS.map((f) => `{{${f}}}`);

/** What VALUTTEN adds to every email, so the design must not. */
export const EDM_GUIDE_ADDED_AUTOMATICALLY =
  'Your logo, brand colour, unsubscribe link and legal footer (business name, ABN, address) are added automatically. Do not add them.';

export interface EdmGuideSection {
  title: string;
  points: string[];
}

/** The guide, as shown on the page and copied into the brief. */
export const EDM_HTML_GUIDE: readonly EdmGuideSection[] = [
  {
    title: 'Layout',
    points: [
      `Build the email from tables, about ${EDM_GUIDE_WIDTH_PX} px wide, single column, centred.`,
      'Put every style inline in a style="" attribute. Style blocks, the <head> and external stylesheets are removed.',
    ],
  },
  {
    title: 'What you can use',
    points: [
      `Tags: ${EDM_GUIDE_ALLOWED_TAGS.join(', ')}.`,
      `Attributes on any tag: ${EDM_GUIDE_ALLOWED_ATTRIBUTES['*'].join(', ')}. Also: ${Object.entries(EDM_GUIDE_ALLOWED_ATTRIBUTES)
        .filter(([tag]) => tag !== '*')
        .map(([tag, attrs]) => `${tag} (${attrs.join(', ')})`)
        .join('; ')}.`,
      `Inline CSS properties: ${EDM_GUIDE_ALLOWED_CSS.join(', ')}. Colours as #hex, rgb() or a colour name; sizes in px, em, rem, % or pt.`,
    ],
  },
  {
    title: 'Images and links',
    points: [
      'Images must use a full https:// address and have alt text. Any other image is removed.',
      'Links must be full https:// addresses (mailto: also works). Put merge fields in text only, never inside a link.',
    ],
  },
  {
    title: 'What is removed',
    points: [
      `Removed with their contents: ${EDM_GUIDE_REMOVED_WITH_CONTENT.join(', ')}.`,
      'Also removed: forms and form fields, event handlers such as onclick, javascript: links, background images and anything using url(), expression(), var() or calc().',
    ],
  },
  {
    title: 'Size',
    points: [
      `At most ${EDM_GUIDE_MAX_BYTES / 1024} KB. Aim for under ${EDM_GUIDE_TARGET_BYTES / 1024} KB so Gmail does not clip it.`,
    ],
  },
  {
    title: 'Merge fields',
    points: [
      `Use ${EDM_GUIDE_MERGE_TOKENS.join(' ')} with double braces exactly as written. Nothing else in double braces is filled.`,
    ],
  },
  {
    title: 'Added for you',
    points: [EDM_GUIDE_ADDED_AUTOMATICALLY],
  },
];

/** The text "Copy brief for Claude" puts on the clipboard. */
export function buildClaudeBrief(): string {
  const lines: string[] = [
    'Please design a marketing email for me as HTML I can paste into my email tool. Follow these rules exactly:',
    '',
  ];
  for (const section of EDM_HTML_GUIDE) {
    lines.push(`${section.title}:`);
    for (const p of section.points) lines.push(`- ${p}`);
    lines.push('');
  }
  lines.push(
    'Reply with the complete HTML only, starting at the outer <table>. My message, brand colours and any https:// image links are below:',
    '',
  );
  return lines.join('\n');
}
